import { Edge } from "@xyflow/react";

import { MyNode, NodeData } from "../types/node-data.types";

import { autoLayoutHorizontal } from "./autoLayoutHorizontal";
import { sanitizeEdges } from "./sanitizeEdges.util";

/** One node as it is stored in `list.campaignConfig` — plain JSON, not a live ReactFlow node. */
export interface StoredCampaignNode {
	id: string;
	position: { x: number; y: number };
	data: Partial<NodeData> & { id: number; label: string };
	[key: string]: unknown;
}

/** One edge as it is stored in `list.campaignConfig`. */
export interface StoredCampaignEdge {
	source: string;
	target: string;
	sourceHandle?: string | null;
	type?: string;
	[key: string]: unknown;
}

export interface HydrateCampaignGraphOptions {
	/** `campaignConfig.initialNodes`; absent for a campaign that has never been saved. */
	nodes?: StoredCampaignNode[] | null;
	/** `campaignConfig.initialEdges`. */
	edges?: StoredCampaignEdge[] | null;
	/** Used when there are no stored nodes — the starter-only graph, positioned by its own page. */
	fallbackNodes: MyNode[];
	/** Page-specific `data` fields (handlers, per-lead schedule state) merged onto every node. */
	decorateNodeData: (node: StoredCampaignNode) => Partial<NodeData>;
	/** Page-specific fields merged onto every edge. */
	decorateEdge?: (edge: StoredCampaignEdge) => Partial<Edge>;
}

/**
 * Turns a stored campaign graph into the live ReactFlow one: re-attaches the callbacks that JSON
 * cannot carry, drops zombie edges, and **re-runs the layout**.
 *
 * The layout runs here rather than only inside the mutation handlers because stored positions are
 * an output, never an input — nodes are `draggable: false`, so nothing but `autoLayoutHorizontal`
 * ever writes them. A graph saved by the old comparator therefore kept its crossed branches on
 * every reload; laying out at hydration means such a campaign draws correctly the moment it opens.
 * Callers seed their autosave baseline from the value returned here, so re-laying out on load costs
 * no write — the corrected coordinates reach the database with the next real edit.
 */
export function hydrateCampaignGraph({
	nodes,
	edges,
	fallbackNodes,
	decorateNodeData,
	decorateEdge,
}: HydrateCampaignGraphOptions): { initialNodes: MyNode[]; initialEdges: Edge[] } {
	const initialEdges = sanitizeEdges<Edge>(
		(edges ?? []).map(
			(edge) =>
				({
					...edge,
					sourceHandle: edge.sourceHandle,
					...decorateEdge?.(edge),
				}) as Edge
		),
		nodes ?? []
	);

	if (!nodes) {
		return { initialNodes: fallbackNodes, initialEdges };
	}

	const hydrated = nodes.map((node) => {
		const { measured, ...rest } = node;

		return {
			...rest,
			data: {
				...rest.data,
				label: node.data.label,
				...decorateNodeData(node),
			},
			position: { x: node.position.x, y: node.position.y },
		} as MyNode;
	});

	return { initialNodes: autoLayoutHorizontal(hydrated, initialEdges), initialEdges };
}
